import type { FormDto, FormVersionDto } from "../services/ApiService.ts";
import { isNil } from "lodash-es";
import { APP_BASE_URL } from "../constants.ts";
import type { Form } from "../state/form.tsx";
import type { FormVersion } from "../state/formVersion.tsx";
import { toFormDto } from "./mappers/form.ts";
import { toFormVersionDto } from "./mappers/formVersion.ts";
import type CustomerId from "../state/CustomerId.ts";

// Bakes in window.location.origin at module load time, not a static literal like PREVIEW_MESSAGE_DATA_SHOW below.
// Safe since the origin can't change without a full page reload, which resets this module anyway.
export const PREVIEW_URL = `${APP_BASE_URL}/preview?parentOrigin=${encodeURIComponent(window.location.origin)}`;
export const PREVIEW_MESSAGE_DATA_SHOW = "onPreviewShow";

export type PreviewPayload = FormDataPayload | TokenPayload | ClosedPayload;

export type FormDataPayload = {
  type: "payload";
  form: FormDto;
  formVersion: FormVersionDto;
  customerId: string;
};

export type TokenPayload = {
  type: "token";
  token: string;
};

export type ClosedPayload = {
  type: "closed";
};

export const sendPayload = (window: Window | null, payload: PreviewPayload) => {
  if (!isNil(window) && !window.closed) {
    const message = JSON.stringify(payload);
    window.postMessage(message, PREVIEW_URL);
  }
};

// App expects previews to be a DRAFT to not throw errors when switching versions (history) & missing fields/variables on the "submission" (created only once when opened)
export const formVersionDtoAsDraft = (formVersionDto: FormVersionDto): FormVersionDto => ({
  ...formVersionDto,
  meta: { ...formVersionDto.meta, status: "DRAFT" },
});

export const sendTokenPayload = (window: Window | null, token: string) => sendPayload(window, { type: "token", token });
export const sendClosedPayload = (window: Window | null) => sendPayload(window, { type: "closed" });
export const sendFormDataPayload = (
  window: Window | null,
  form: FormDto,
  formVersion: FormVersionDto,
  customerId: CustomerId,
) =>
  sendPayload(window, {
    type: "payload",
    form,
    formVersion: formVersionDtoAsDraft(formVersion),
    customerId: customerId.toString(),
  });
export const sendMappedFormDataPayload = (
  window: Window | null,
  form: Form,
  formVersion: FormVersion,
  customerId: CustomerId,
) => sendFormDataPayload(window, toFormDto(form), toFormVersionDto(formVersion), customerId);
